import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { Button, Field } from "../components/ui";
import { useAuth } from "../auth";
import { apiGet, apiSend } from "../api";
import { formatYmd } from "../dateutil";
import { C, R, S } from "../theme";

const empty = { name: "", description: "" };

export default function StaffTraining() {
  const { id } = useParams();
  const { isManager } = useAuth();
  const nav = useNavigate();
  const [attraction, setAttraction] = useState(null);
  const [topics, setTopics] = useState([]);
  const [records, setRecords] = useState([]);
  const [modal, setModal] = useState(null); // null | {create} | {edit,item}
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadTopics = () => apiGet(`/attractions/${id}/training-topics`).then(setTopics).catch(() => {});
  useEffect(() => {
    apiGet(`/attractions/${id}`).then(setAttraction).catch(() => {});
    apiGet(`/attractions/${id}/training`).then(setRecords).catch(() => {});
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openCreate = () => { setForm(empty); setErr(""); setModal({ create: true }); };
  const openEdit = (it) => { setForm({ name: it.name, description: it.description || "" }); setErr(""); setModal({ edit: true, item: it }); };
  const save = async () => {
    if (!form.name.trim()) { setErr("Topic name is required"); return; }
    setBusy(true); setErr("");
    const payload = { name: form.name.trim(), description: form.description.trim() || null };
    try {
      if (modal.edit) await apiSend(`/training-topics/${modal.item.id}`, "PATCH", payload);
      else await apiSend(`/attractions/${id}/training-topics`, "POST", payload);
      setModal(null); await loadTopics();
    } catch (e) { setErr(e?.message || "Save failed"); } finally { setBusy(false); }
  };
  const remove = async (it) => { if (!window.confirm(`Delete training topic "${it.name}"?`)) return; await apiSend(`/training-topics/${it.id}`, "DELETE"); loadTopics(); };

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button data-testid="training-back" className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Staff Training</div>
      </div>
      <div style={{ padding: S.lg }}>
        {attraction && <div style={{ color: C.muted, marginBottom: S.md }}>{attraction.name}</div>}

        <Button title="＋ Record Staff Training" testID="record-training-btn" variant="green" onPress={() => nav(`/training/new/${id}`)} />

        <div style={{ color: C.text, fontSize: 18, fontWeight: 900, marginTop: S.xl, marginBottom: 4 }}>Training Topics</div>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: S.md }}>Six standard topics are ready to go.{isManager ? " Add your own custom topics below." : ""}</div>

        <div data-testid="topic-list">
          {topics.map((it) => (
            <div key={it.id} data-testid={`topic-${it.id}`} style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}` }}>
              <Icon name="school" size={18} color={C.blue} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.text, fontWeight: 800, fontSize: 15 }}>{it.name}{it.is_default && <span style={{ background: C.card2, color: C.muted, fontSize: 9, fontWeight: 800, marginLeft: 8, letterSpacing: 0.5, padding: "2px 7px", borderRadius: R.sm, verticalAlign: "middle" }}>STANDARD</span>}</div>
                {!!it.description && <div style={{ color: C.sub, fontSize: 13, marginTop: 2, lineHeight: 1.4 }}>{it.description}</div>}
              </div>
              {isManager && (
                <div style={{ display: "flex", gap: 12 }}>
                  <button data-testid={`edit-topic-${it.id}`} onClick={() => openEdit(it)} style={{ background: "transparent", border: "none", cursor: "pointer" }}><Icon name="create-outline" size={18} color={C.amber} /></button>
                  <button data-testid={`delete-topic-${it.id}`} onClick={() => remove(it)} style={{ background: "transparent", border: "none", cursor: "pointer" }}><Icon name="trash" size={18} color={C.red} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
        {isManager && (
          <button data-testid="add-topic-btn" className="pressable" onClick={openCreate} style={{ marginTop: S.sm, width: "100%", background: C.card2, borderRadius: R.md, minHeight: 50, display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, color: C.amber, fontWeight: 800, fontSize: 15, border: `1px solid ${C.borderStrong}`, cursor: "pointer" }}>
            <Icon name="add" size={22} color={C.amber} /> Add Custom Topic
          </button>
        )}

        <div style={{ color: C.text, fontSize: 18, fontWeight: 900, marginTop: S.xl, marginBottom: S.sm }}>Training Records</div>
        {records.length === 0 ? (
          <div style={{ textAlign: "center", padding: `${S.lg}px 0`, color: C.muted }}><Icon name="school-outline" size={40} color={C.muted} /><div style={{ marginTop: S.sm, fontSize: 14 }}>No training recorded yet</div></div>
        ) : records.map((t) => (
          <div key={t.id} data-testid={`train-rec-${t.id}`} className="pressable" onClick={() => nav(`/training/${t.id}`)} style={{ display: "flex", alignItems: "center", gap: S.md, background: C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}`, cursor: "pointer" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="school" size={20} color={C.green} /></div>
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ color: C.text, fontWeight: 700 }}>{t.staff_name}</div><div style={{ color: C.muted, fontSize: 12 }}>{formatYmd(t.date_trained)} · {(t.topics || t.competencies || []).length} topic(s) · Trainer {t.trainer_name}</div></div>
            <Icon name="chevron-forward" size={16} color={C.muted} />
          </div>
        ))}
        <div style={{ height: S.xxl }} />
      </div>

      {modal && (
        <div className="modal-backdrop modal-center" onClick={() => setModal(null)}>
          <div data-testid="topic-modal" onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: R.lg, padding: S.xl, width: "100%", maxWidth: 420, border: `1px solid ${C.borderStrong}` }}>
            <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: S.md }}>{modal.edit ? "Edit Topic" : "Add Custom Topic"}</div>
            <Field label="Topic name" testID="topic-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ride control-panel operation" />
            <Field label="Description (optional)" testID="topic-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this training covers" />
            {!!err && <div style={{ color: C.red, fontWeight: 700, marginBottom: S.sm }}>{err}</div>}
            <Button title={modal.edit ? "Save Changes" : "Add Topic"} testID="topic-save" variant="green" onPress={save} loading={busy} />
            <button className="pressable" onClick={() => setModal(null)} style={{ color: C.muted, fontWeight: 700, fontSize: 15, marginTop: S.md, width: "100%", background: "transparent", border: "none", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
